import React from 'react';
import {useWindowDimensions, View} from 'react-native';
import {noop} from 'lodash';
import {get} from 'lodash';

import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import {DashboardID} from '/constant/TestIDs';
import {BonusesID} from '/constant/TestIDs';
import {DoubleDoubleEndDate, DoubleDoubleStartDate} from '/enum/dates.enum';
import {useTranslation} from '/provider/TranslationProvider';
import {getDateUtilDefaultInstance} from '/util/date.util';
import {parse as parseJSON} from '/util/json.util';
import testProperties from '/util/testing/testProperties';

import {styles} from './CoachAcceleratorBonusBanner.style';
import {
  CAB_TIER_SIZE,
  calculateBonusPeriod,
  calculateDaysLeft,
  generateOnLearnMore,
  getCappedClientCount,
  getTierProgress,
  getTierSlots,
} from './CoachAcceleratorBonusBanner.util';

export const CoachAcceleratorBonusBanner = ({
  doubleDoubleBonusPeriod = '{}',
  currentScreen = '',
  totalOECount = 0,
  currentDate = '',
  popoverShow = noop,
  cardNumber = null,
  totalCards = null,
  bannerHeight = null,
  bannerText = {
    CABText: '',
    DoubleDoubleText: '',
  },
  endDate = '',
  handleBannerHeight = noop,
}: any) => {
  const {ts, tt} = useTranslation('coachAcceleratorBonusBanner');
  const {fontScale} = useWindowDimensions();
  const isLargeFont = fontScale > 1.15;
  const slotMinHeight = Math.min(36 + (fontScale - 1) * 14, 56);

  const parsedDoubleDoubleBonusPeriod =
    typeof doubleDoubleBonusPeriod === 'string'
      ? parseJSON(doubleDoubleBonusPeriod) || {}
      : doubleDoubleBonusPeriod || {};

  const {
    DoubleDoubleStartDate: DDBStartDate = DoubleDoubleStartDate,
    DoubleDoubleEndDate: DDBEndDate = DoubleDoubleEndDate,
  } = parsedDoubleDoubleBonusPeriod;

  const dateUtil = getDateUtilDefaultInstance();
  const isDoublePeriod = dateUtil.isWithinInterval(
    currentDate,
    DDBStartDate,
    DDBEndDate,
  );

  const isWithinTwoMonthsAfterDDBEndDate =
    endDate &&
    dateUtil.isDateWithinTwoMonthsPeriod(
      DDBEndDate,
      dateUtil.getLastDayOfPreviousMonth(endDate),
    );

  const primaryText =
    isDoublePeriod || isWithinTwoMonthsAfterDDBEndDate
      ? get(
          bannerText,
          'CABV2Text.primaryText',
          'The total number of new Clients is cumulative over the eligibility period.',
        )
      : get(
          bannerText,
          'CABText.primaryText',
          'The total number of new Clients is cumulative over the eligibility period.',
        );
  const secondaryText =
    isDoublePeriod || isWithinTwoMonthsAfterDDBEndDate
      ? get(
          bannerText,
          'CABV2Text.secondaryText',
          'The total bonus has a max earnings of $3,500.',
        )
      : get(
          bannerText,
          'CABText.secondaryText',
          'The total bonus has a max earnings of $3,500.',
        );

  const bonusPeriod = calculateBonusPeriod(endDate);
  const cappedClientCount = getCappedClientCount(totalOECount);
  const daysLeft = calculateDaysLeft(currentDate, endDate);
  const currentMonth = dateUtil.formatDate(
    currentDate || new Date().toISOString(),
    'MMMM yyyy',
  );

  const URL =
    isDoublePeriod || isWithinTwoMonthsAfterDDBEndDate
      ? get(
          bannerText,
          'CABV2Text.URL',
          'https://coachanswers.optavia.com/help/s/article/increased-bonus-payout-program?language=en_US',
        )
      : get(
          bannerText,
          'CABText.URL',
          'https://coachanswers.optavia.com/help/s/article/integrated-compensation-plan-update-us?language=en_US#c-a-b',
        );

  const descriptionText = [primaryText, secondaryText, bonusPeriod]
    .map(text => `${text || ''}`.trim())
    .filter(Boolean)
    .join(' ')
    .replace(/[ \t]{2,}/g, ' ');

  const renderDescription = () => {
    return (
      <>
        {tt('content', () => (
          <View accessible={false}>
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              style={styles.descriptionText}
              {...testProperties(DashboardID.dashboardCabBannerDescription)}
              accessible={true}
              accessibilityRole="text"
              accessibilityLabel={`${descriptionText}.`}>
              {descriptionText}.
            </Text>

            {tt('learnMore', () => (
              <TouchableOpacity
                {...testProperties(DashboardID.dashboardCabBannerLearnMoreLink)}
                accessible={true}
                accessibilityRole="button"
                accessibilityLabel="Learn more about Coach Accelerator Bonus"
                onPress={generateOnLearnMore(
                  {popoverShow},
                  currentScreen,
                  URL,
                )}>
                <Text
                  allowFontScaling
                  maxFontSizeMultiplier={2}
                  style={styles.link}
                  accessible={true}>
                  Learn more
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        ))}
      </>
    );
  };

  const renderTierRow = (
    bonusNumber: number,
    amountLabel: string,
    tierIndex: number,
  ) => {
    const progress = getTierProgress(cappedClientCount, tierIndex);
    const isComplete = progress === CAB_TIER_SIZE;
    const isActive = progress > 0;
    const tierStatusLabel = isComplete
      ? 'Complete'
      : isActive
      ? `${progress}/${CAB_TIER_SIZE}`
      : 'Pending';

    return (
      <View
        key={bonusNumber}
        style={styles.tierBlock}
        testID={BonusesID.bonusTier}>
        <View
          style={[
            styles.tierHeaderRow,
            isLargeFont && styles.tierHeaderRowLargeFont,
          ]}
          testID={BonusesID.bonusTierHeader}>
          <Text
            allowFontScaling
            maxFontSizeMultiplier={2}
            variant="button"
            style={styles.tierTitleText}
            testID={BonusesID.bonusTitle}
            accessible={true}
            accessibilityRole="text">
            {`Bonus ${bonusNumber}`}
          </Text>
          <Text
            allowFontScaling
            maxFontSizeMultiplier={2}
            variant="body"
            style={[
              styles.tierAmountText,
              isLargeFont && styles.tierAmountTextLargeFont,
            ]}
            testID={BonusesID.bonusAmount}
            accessible={true}
            accessibilityRole="text">
            {amountLabel}
          </Text>
          <View
            style={[
              styles.tierStatus,
              isLargeFont && styles.tierStatusLargeFont,
            ]}
            testID={BonusesID.bonusCompleteBadge}
            accessible={true}
            accessibilityRole="text"
            accessibilityLabel={`Bonus ${bonusNumber} status ${tierStatusLabel}`}>
            {(isComplete || isActive) && (
              <Icon
                name="check"
                style={styles.tierStatusIcon}
                accessible={false}
                accessibilityElementsHidden={true}
                importantForAccessibility="no-hide-descendants"
              />
            )}
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              variant="caption"
              style={styles.tierStatusText}
              accessible={false}
              accessibilityElementsHidden={true}
              importantForAccessibility="no-hide-descendants">
              {tierStatusLabel}
            </Text>
          </View>
        </View>

        <View style={styles.tierSlotsRow}>
          {getTierSlots(tierIndex).map((slotNumber, index, slots) => {
            const isFilled = slotNumber <= cappedClientCount;
            const isLastSlot = index === slots.length - 1;
            const slotAccessibilityLabel = `Bonus ${bonusNumber}, Client ${slotNumber}`;

            return (
              <View
                key={slotNumber}
                accessible={true}
                focusable={true}
                testID={`bonus-slot-${slotNumber}`}
                accessibilityRole="checkbox"
                accessibilityLabel={slotAccessibilityLabel}
                accessibilityState={{checked: isFilled}}
                style={[
                  styles.slot,
                  {minHeight: slotMinHeight, height: 'auto'},
                  isFilled && styles.slotFilled,
                  isLastSlot && styles.slotLast,
                ]}>
                {isFilled ? (
                  <View
                    style={styles.slotFilledContent}
                    accessible={false}
                    accessibilityElementsHidden={true}
                    importantForAccessibility="no-hide-descendants">
                    <Text
                      allowFontScaling
                      maxFontSizeMultiplier={2}
                      variant="caption"
                      style={styles.slotFilledNumber}
                      accessible={false}>
                      {slotNumber}
                    </Text>
                    <Icon
                      name="check"
                      style={styles.slotCheckIcon}
                      accessible={false}
                      accessibilityElementsHidden={true}
                      importantForAccessibility="no-hide-descendants"
                    />
                  </View>
                ) : (
                  <Text
                    allowFontScaling
                    maxFontSizeMultiplier={2}
                    variant="caption"
                    style={styles.slotPendingNumber}
                    accessible={false}
                    accessibilityElementsHidden={true}
                    importantForAccessibility="no-hide-descendants">
                    {slotNumber}
                  </Text>
                )}
              </View>
            );
          })}
        </View>
      </View>
    );
  };

  return (
    <View
      onLayout={handleBannerHeight}
      style={{
        ...styles.AcceleratorBonusContent,
        ...(bannerHeight ? {minHeight: bannerHeight} : {}),
      }}
      {...testProperties(DashboardID.dashboardCabBanner)}
      accessible={false}>
      <View style={styles.headerContainer} accessible={false}>
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          variant="heading"
          style={styles.headerText}
          {...testProperties(DashboardID.dashboardCabBannerTitle)}
          accessible={true}
          accessibilityLabel={'Coach Accelerator Bonus'}>
          {tt('heading', 'Coach Accelerator Bonus')}
        </Text>
        {totalCards &&
          cardNumber &&
          tt('sideTitle', () => (
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              variant="button"
              style={styles.sideText}
              {...testProperties(DashboardID.dashboardCabBannerSideTitle)}
              accessible={true}
              accessibilityRole="text"
              accessibilityLabel={`slide ${cardNumber} of ${totalCards}`}>
              {cardNumber} of {totalCards}
            </Text>
          ))}
      </View>
      {renderDescription()}
      <View style={styles.divider} />
      <View style={styles.metaRow}>
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          variant="heading"
          style={styles.monthText}
          testID={BonusesID.periodMonthLabel}
          accessible={true}
          accessibilityRole="text">
          {currentMonth}
        </Text>
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          variant="body"
          style={styles.daysLeftText}
          testID={BonusesID.daysLeftLabel}
          accessible={true}
          accessibilityRole="text">
          {ts('daysLeft', `${daysLeft} days left`)}
        </Text>
      </View>

      <Text
        allowFontScaling
        maxFontSizeMultiplier={2}
        variant="caption"
        style={styles.pqvHeaderText}
        accessible={true}
        accessibilityRole="text"
        {...testProperties(DashboardID.dashboardProgressBarLabel)}>
        {ts('newClientsWith', 'New Clients with at least 275 PQV:')}
      </Text>

      {renderTierRow(1, '$500 bonus', 0)}
      {renderTierRow(2, '$1,000 bonus', 1)}
      {renderTierRow(3, '$2,000 bonus', 2)}
    </View>
  );
};
